/**
 * Header/footer editing (see PageSetupBtn.tsx for why all three of these are toolbar popovers
 * rather than the side panels the plan first described). A popover with six mini ProseMirror
 * editor instances (one per
 * DocSection header/footer slot), reusing the exact same `cdmExtensions` schema and
 * `blocksToPm`/`pmToBlocks` conversion functions the main editor uses — a header/footer is just a
 * `DocBlock[]` (`HeaderFooter.content`), so it needs no new schema or conversion logic of its own.
 *
 * A mini editor's own top-level content still has to satisfy `cdmExtensions`' `doc: 'section+'`
 * content rule (see schema.ts), so each one is wrapped in a single synthetic `section` node on
 * load and unwrapped back to a flat block list on save — the section's own attrs (`page` etc.)
 * are throwaway placeholders here, never read back into the real document.
 *
 * All six of `DocSection`'s slots (`Header`/`Footer` plus the `FirstPage*`/`EvenPage*` variants)
 * are editable here. The CDM carries no separate "different first page" / "different odd and
 * even pages" boolean anywhere (see `CanonicalDocument.cs`'s `DocSection`) — presence-vs-null on
 * `firstPageHeader`/`firstPageFooter`/`evenPageHeader`/`evenPageFooter` IS the flag, matching how
 * the DOCX parser/renderer derive `w:titlePg`/`w:evenAndOddHeaders` purely from whether those
 * fields are populated. So "turning on" a First Page or Even Pages variant here means writing an
 * empty-but-present `HeaderFooter{content: []}`; "turning it off" means writing `null` back —
 * there is no separate flag to keep in sync with the content.
 *
 * UI shape: a primary Header/Footer tab (as before) crossed with a secondary All-pages/First-page/
 * Even-pages variant selector — six slots total, addressed by (primary, variant). The two
 * unconditional All-pages slots are always editable; the four variant slots show an "Add" prompt
 * until enabled and a "Remove" action once they are, since an unpopulated variant genuinely means
 * "no override — fall back to the normal header/footer," not "empty content."
 *
 * Each mini editor also gets a compact Bold/Italic/Underline row (`MiniFormatToolbar.tsx`, shared
 * with `FootnoteEndnoteBtn.tsx`'s note editor) alongside variable insertion — `{{page_number}}`-
 * style fields remain the single most common real header/footer content, but full rich-text
 * formatting there was flagged as "a follow-up" and has since landed; the main toolbar's fuller
 * set (font/size/color/alignment/lists/etc.) is deliberately not replicated here, since these
 * editors hold a line or two of text, not a full document. Edits apply back to the main editor's
 * `section` node on blur (not per-keystroke, which would spam the main document's undo history)
 * and once more when the popover closes or the active tab/variant changes, as a safety net.
 */
import { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileLines, faPlus } from '@fortawesome/free-solid-svg-icons'
import { useEditor, EditorContent, type JSONContent } from '@tiptap/react'
import type { useEditor as useEditorType } from '@tiptap/react'
import { colors, fontSize, radius, spacing } from '../../styles/theme'
import { PopoverBtn } from './ToolbarControls'
import { MiniFormatToolbar } from './MiniFormatToolbar'
import { cdmExtensions } from '../../cdm/schema'
import { blocksToPm } from '../../cdm/fromCdm'
import { pmToBlocks } from '../../cdm/toCdm'
import { normalizeMonikerKey } from '../../cdm/monikerKey'
import {
  defaultParagraphFormat, defaultPageSetup, defaultRunFormat, defaultSourceLocation,
  type HeaderFooter,
} from '../../cdm/types'

type CdmEditor = ReturnType<typeof useEditorType>

type Primary = 'header' | 'footer'
type Variant = 'normal' | 'first' | 'even'
type SlotKey = 'header' | 'footer' | 'firstPageHeader' | 'firstPageFooter' | 'evenPageHeader' | 'evenPageFooter'
type NullableSlotKey = 'firstPageHeader' | 'firstPageFooter' | 'evenPageHeader' | 'evenPageFooter'

const SLOT_KEY: Record<Primary, Record<Variant, SlotKey>> = {
  header: { normal: 'header', first: 'firstPageHeader', even: 'evenPageHeader' },
  footer: { normal: 'footer', first: 'firstPageFooter', even: 'evenPageFooter' },
}

const VARIANT_LABEL: Record<Variant, string> = { normal: 'All pages', first: 'First page', even: 'Even pages' }

function isNullableSlot(key: SlotKey): key is NullableSlotKey {
  return key !== 'header' && key !== 'footer'
}

function headerFooterToDoc(hf: HeaderFooter | null | undefined): JSONContent {
  const pmBlocks = blocksToPm(hf?.content ?? [])
  const content = pmBlocks.length > 0 ? pmBlocks : [{ type: 'paragraph', attrs: { format: defaultParagraphFormat() }, content: [] }]
  return {
    type: 'doc',
    content: [{
      type: 'section',
      // Placeholder attrs only — this synthetic section exists purely to satisfy the schema's
      // `doc: section+` content rule; nothing here is ever read back into the real document.
      attrs: { page: defaultPageSetup(), header: null, footer: null, firstPageHeader: null, firstPageFooter: null, evenPageHeader: null, evenPageFooter: null },
      content,
    }],
  }
}

function docToHeaderFooter(doc: JSONContent): HeaderFooter {
  return { content: pmToBlocks(doc.content?.[0]?.content ?? []) }
}

function MiniEditorTab({ editor, varName, setVarName, onInsertVar }: {
  editor: CdmEditor
  varName: string
  setVarName: (v: string) => void
  onInsertVar: () => void
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
      <MiniFormatToolbar editor={editor} />
      <div
        className="cdm-editor"
        style={{ border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: spacing.sm, minHeight: 80, maxHeight: 200, overflow: 'auto', background: colors.surface }}
      >
        <EditorContent editor={editor} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: fontSize.xs, color: colors.textSecondary, fontFamily: 'monospace' }}>{'{{x}}'}</span>
        <input
          type="text"
          value={varName}
          onChange={e => setVarName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onInsertVar() } }}
          placeholder="var_name"
          style={{ padding: '3px 6px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, width: 110, outline: 'none' }}
        />
        <button type="button" onClick={onInsertVar} style={{ padding: '3px 8px', border: `1px solid ${colors.primary}`, borderRadius: radius.sm, background: colors.primaryLight, color: colors.primary, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer' }}>
          Insert
        </button>
      </div>
    </div>
  )
}

export default function HeaderFooterBtn({ mainEditor }: { mainEditor: CdmEditor }) {
  const [open, setOpen] = useState(false)
  const [primary, setPrimary] = useState<Primary>('header')
  const [variant, setVariant] = useState<Variant>('normal')
  const [varNames, setVarNames] = useState<Record<SlotKey, string>>({
    header: '', footer: '', firstPageHeader: '', firstPageFooter: '', evenPageHeader: '', evenPageFooter: '',
  })
  const [enabled, setEnabled] = useState<Record<NullableSlotKey, boolean>>({
    firstPageHeader: false, firstPageFooter: false, evenPageHeader: false, evenPageFooter: false,
  })

  const headerEditor = useEditor({ extensions: cdmExtensions, content: headerFooterToDoc(null) })
  const footerEditor = useEditor({ extensions: cdmExtensions, content: headerFooterToDoc(null) })
  const firstPageHeaderEditor = useEditor({ extensions: cdmExtensions, content: headerFooterToDoc(null) })
  const firstPageFooterEditor = useEditor({ extensions: cdmExtensions, content: headerFooterToDoc(null) })
  const evenPageHeaderEditor = useEditor({ extensions: cdmExtensions, content: headerFooterToDoc(null) })
  const evenPageFooterEditor = useEditor({ extensions: cdmExtensions, content: headerFooterToDoc(null) })

  const editors: Record<SlotKey, CdmEditor> = {
    header: headerEditor,
    footer: footerEditor,
    firstPageHeader: firstPageHeaderEditor,
    firstPageFooter: firstPageFooterEditor,
    evenPageHeader: evenPageHeaderEditor,
    evenPageFooter: evenPageFooterEditor,
  }

  function writeSlot(key: SlotKey, value: HeaderFooter | null) {
    mainEditor?.chain().updateAttributes('section', { [key]: value }).run()
  }

  // Flushes a mini editor's current content back to the main document. Nullable slots are
  // guarded by `enabled` so a stray blur on a disabled (unmounted) variant editor can never
  // resurrect it — only `enableVariant`/`disableVariant` are allowed to change a slot's
  // null-vs-populated state itself.
  function applySlot(key: SlotKey) {
    if (isNullableSlot(key) && !enabled[key]) return
    const ed = editors[key]
    if (!ed || !mainEditor) return
    writeSlot(key, docToHeaderFooter(ed.getJSON()))
  }

  function applyAllEnabled() {
    (Object.keys(editors) as SlotKey[]).forEach(applySlot)
  }

  function enableVariant(key: NullableSlotKey) {
    editors[key]?.commands.setContent(headerFooterToDoc(null))
    writeSlot(key, { content: [] })
    setEnabled(e => ({ ...e, [key]: true }))
  }

  function disableVariant(key: NullableSlotKey) {
    writeSlot(key, null)
    setEnabled(e => ({ ...e, [key]: false }))
  }

  useEffect(() => {
    const keys = Object.keys(editors) as SlotKey[]
    const entries = keys.map(key => {
      const handler = () => applySlot(key)
      editors[key]?.on('blur', handler)
      return { key, handler }
    })
    return () => {
      entries.forEach(({ key, handler }) => editors[key]?.off('blur', handler))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerEditor, footerEditor, firstPageHeaderEditor, firstPageFooterEditor, evenPageHeaderEditor, evenPageFooterEditor, enabled])

  function loadFromMain() {
    const sectionAttrs = mainEditor?.getJSON().content?.[0]?.attrs
    ;(Object.keys(editors) as SlotKey[]).forEach(key => {
      editors[key]?.commands.setContent(headerFooterToDoc(sectionAttrs?.[key] as HeaderFooter | null))
    })
    setEnabled({
      firstPageHeader: sectionAttrs?.firstPageHeader != null,
      firstPageFooter: sectionAttrs?.firstPageFooter != null,
      evenPageHeader: sectionAttrs?.evenPageHeader != null,
      evenPageFooter: sectionAttrs?.evenPageFooter != null,
    })
  }

  function switchTo(nextPrimary: Primary, nextVariant: Variant) {
    applySlot(SLOT_KEY[primary][variant])
    setPrimary(nextPrimary)
    setVariant(nextVariant)
  }

  function insertVar(key: SlotKey) {
    const editor = editors[key]
    const rawName = varNames[key].trim()
    if (!rawName || !editor) return
    editor.chain().focus().insertContent({
      type: 'placeholder',
      attrs: { rawName, normalizedKey: normalizeMonikerKey(rawName), formatters: null, format: defaultRunFormat(), source: defaultSourceLocation() },
    }).run()
    setVarNames(v => ({ ...v, [key]: '' }))
  }

  const activeKey = SLOT_KEY[primary][variant]
  const activeIsEnabled = !isNullableSlot(activeKey) || enabled[activeKey]

  return (
    <PopoverBtn
      icon={faFileLines}
      tooltipText="Header & footer"
      active={open}
      onActivate={() => { setOpen(true); loadFromMain() }}
      onDeactivate={() => { applyAllEnabled(); setOpen(false) }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, minWidth: 340 }}>
        <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}` }}>
          {(['header', 'footer'] as Primary[]).map(p => (
            <button
              key={p}
              type="button"
              onClick={() => switchTo(p, variant)}
              style={{ padding: '4px 12px', border: 'none', borderBottom: `2px solid ${primary === p ? colors.primary : 'transparent'}`, background: 'none', fontSize: fontSize.sm, fontWeight: primary === p ? 600 : 400, color: primary === p ? colors.primary : colors.textSecondary, cursor: 'pointer', textTransform: 'capitalize' }}
            >
              {p}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 4 }}>
          {(['normal', 'first', 'even'] as Variant[]).map(v => (
            <button
              key={v}
              type="button"
              onClick={() => switchTo(primary, v)}
              style={{ padding: '2px 8px', border: `1px solid ${variant === v ? colors.primary : colors.border}`, borderRadius: radius.sm, background: variant === v ? colors.primaryLight : 'none', color: variant === v ? colors.primary : colors.text, fontSize: fontSize.xs, fontWeight: variant === v ? 600 : 400, cursor: 'pointer' }}
            >
              {VARIANT_LABEL[v]}
            </button>
          ))}
        </div>

        {activeIsEnabled ? (
          <>
            <MiniEditorTab
              editor={editors[activeKey]}
              varName={varNames[activeKey]}
              setVarName={v => setVarNames(n => ({ ...n, [activeKey]: v }))}
              onInsertVar={() => insertVar(activeKey)}
            />
            {isNullableSlot(activeKey) && (
              <button
                type="button"
                onClick={() => disableVariant(activeKey)}
                style={{ alignSelf: 'flex-start', padding: 0, border: 'none', background: 'none', color: colors.textMuted, fontSize: fontSize.xs, textDecoration: 'underline', cursor: 'pointer' }}
              >
                Remove — use the same {primary} as All pages
              </button>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: spacing.sm, padding: spacing.sm, textAlign: 'center' }}>
            <span style={{ fontSize: fontSize.sm, color: colors.textMuted }}>
              This section uses the same {primary} on {variant === 'first' ? 'the first page as the rest of the document' : 'even and odd pages'}.
            </span>
            <button
              type="button"
              onClick={() => enableVariant(activeKey as NullableSlotKey)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', border: `1px solid ${colors.primary}`, borderRadius: radius.sm, background: colors.primaryLight, color: colors.primary, fontSize: fontSize.sm, fontWeight: 600, cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faPlus} /> Add {variant === 'first' ? 'a first-page' : 'an even-page'} {primary}
            </button>
          </div>
        )}

        <div style={{ fontSize: fontSize.xs, color: colors.textMuted }}>
          All pages is shown unless a First page or Even pages override is added above (Word&apos;s
          title-page / odd-even settings — added automatically when you add one here).
        </div>
      </div>
    </PopoverBtn>
  )
}
